import axios from 'axios';

const DICOMWEB_URL = '/dicom-web';
const RENDERING_URL = '/render';
const SEARCH_URL = '/api/search';
const ADMIN_URL = '/api/admin';
const REPORTING_URL = SEARCH_URL;
// Provisional frontend contract: QC ticket persistence is not implemented yet, so
// this routes through the existing authenticated viewer/search proxy; callers should
// keep surfacing request failures until a dedicated backend endpoint (for example
// /api/qc/tickets) is added.
const QC_TICKET_URL = SEARCH_URL;
// Coalesce only the immediate worklist -> viewer route handoff. The caches are
// memory-only, small, short-lived, and keyed by study so deep links still issue
// their own authenticated requests.
const STUDY_OPEN_HANDOFF_TTL_MS = 1500;
const STUDY_OPEN_HANDOFF_MAX = 16;
const WARMUP_TRIGGER_TTL_MS = 3000;
const studyOpenBundleHandoffs = new Map();
const studyWarmupTriggers = new Map();

const putBoundedHandoff = (map, key, value) => {
  if (!map.has(key) && map.size >= STUDY_OPEN_HANDOFF_MAX) {
    map.delete(map.keys().next().value);
  }
  map.set(key, value);
};

const getFreshHandoff = (map, key) => {
  const entry = map.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    map.delete(key);
    return null;
  }
  return entry;
};

// Explicit reset seam for deterministic unit isolation. Runtime auth/session
// teardown may also call this if same-document logout behavior is introduced.
export const resetStudyOpenRequestHandoffs = () => {
  studyOpenBundleHandoffs.clear();
  studyWarmupTriggers.clear();
};
// dcm4chee DICOMweb doesn't require Basic auth


const authAxios = axios;  // dcm4chee DICOMweb: no Basic auth needed
const isAuthorizationFailure = (error) => [401, 403].includes(error?.response?.status);

const tag = (obj, key, fallback = '') => {
  try { return obj[key]?.Value?.[0] ?? fallback; } catch { return fallback; }
};
const tagArr = (obj, key) => {
  try { return obj[key]?.Value ?? []; } catch { return []; }
};
const tagName = (obj, key, fallback = '') => {
  try { return obj[key]?.Value?.[0]?.Alphabetic ?? fallback; } catch { return fallback; }
};
// DICOM (0008,0080) is Institution Name; surface it as a compact non-PHI site cue.
const INSTITUTION_NAME_TAG = '00080080';

// ─── Search API (PostgreSQL) ──────────────────────────────────────────────────

export const getStudies = async (params = {}) => {
  try {
    const qp = new URLSearchParams();
    if (params.q) qp.set('q', params.q);
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.offset) qp.set('offset', String(params.offset));
    if (params.modality) qp.set('modality', params.modality);
    const res = await axios.get(`${SEARCH_URL}/studies?${qp.toString()}`);
    return res.data || [];
  } catch (e) {
    if (isAuthorizationFailure(e)) throw e;
    console.error('Search API failed, falling back to DICOMweb:', e);
    return getStudiesDICOMweb();
  }
};

export const getStudiesPage = async (params = {}) => {
  const qp = new URLSearchParams();
  if (params.q) qp.set('q', params.q);
  if (params.limit) qp.set('limit', String(params.limit));
  if (params.modality) qp.set('modality', params.modality);
  if (params.facilityId) qp.set('facility_id', params.facilityId);
  if (params.sort) qp.set('sort', params.sort);
  if (params.cursor) qp.set('cursor', params.cursor);
  const suffix = qp.toString() ? `?${qp.toString()}` : '';
  const res = await axios.get(`${SEARCH_URL}/studies/page${suffix}`);
  const page = res.data;
  const valid = page
    && page.schemaVersion === 1
    && Array.isArray(page.studies)
    && typeof page.hasMore === 'boolean'
    && Number.isInteger(page.pageSize)
    && page.pageSize >= 1
    && page.pageSize <= 500
    && (page.nextCursor === null || typeof page.nextCursor === 'string')
    && (!page.hasMore || Boolean(page.nextCursor));
  if (!valid) throw new Error('Unsupported study-page schema');
  return page;
};

// Fallback: fetch studies via dcm4chee DICOMweb (QIDO-RS via /dicom-web nginx proxy)
const getStudiesDICOMweb = async () => {
  try {
    const res = await authAxios.get(`${DICOMWEB_URL}/studies`);
    return (res.data || []).map(s => ({
      studyInstanceUID: tag(s, '0020000D'),
      patientName:      tagName(s, '00100010', 'Unknown Patient'),
      patientId:        tag(s, '00100020'),
      accessionNumber:  tag(s, '00080050'),
      studyDate:        tag(s, '00080020'),
      studyTime:        tag(s, '00080030'),
      studyDescription: tag(s, '00081030', ''),
      modalities:       tagArr(s, '00080061').join('/'),
      numSeries:        tag(s, '00201206', '?'),
      numInstances:     tag(s, '00201208', '?'),
      receivedOn:       '',
      sourceAE:         '',
      facilityName:     '',
      institutionName:  tag(s, INSTITUTION_NAME_TAG),
      stationName:      tag(s, '00081010'),
      storageTier:      'hot',
    })).filter(s => s.studyInstanceUID);
  } catch (e) {
    console.error('Error fetching studies:', e);
    return [];
  }
};

// ─── Series (via dcm4chee DICOMweb / gqg-search) ────────────────────────────

export const normalizeProtocolWindows = (protocolWindows = []) => {
  const distinct = new Map();
  (Array.isArray(protocolWindows) ? protocolWindows : []).forEach((window) => {
    const seriesInstanceUID = typeof window?.seriesInstanceUID === 'string'
      ? window.seriesInstanceUID.trim()
      : '';
    const ww = Number.isFinite(Number(window?.ww)) ? Math.round(Number(window.ww)) : NaN;
    const wc = Number.isFinite(Number(window?.wc)) ? Math.round(Number(window.wc)) : NaN;
    if (!seriesInstanceUID || !Number.isInteger(ww) || !Number.isInteger(wc)) return;
    if (ww < 1 || ww > 100000 || wc < -100000 || wc > 100000) return;
    distinct.set(`${seriesInstanceUID}\u0000${ww}\u0000${wc}`, { seriesInstanceUID, ww, wc });
  });
  return [...distinct.values()]
    .sort((a, b) => a.seriesInstanceUID.localeCompare(b.seriesInstanceUID) || a.ww - b.ww || a.wc - b.wc)
    .slice(0, 16);
};

export const triggerStudyRenderWarmup = (studyInstanceUID, protocolWindows = []) => {
  const normalizedWindows = normalizeProtocolWindows(protocolWindows);
  const signature = normalizedWindows.length ? JSON.stringify(normalizedWindows) : '';
  const handoffKey = `${studyInstanceUID}\u0000${signature}`;
  const cached = getFreshHandoff(studyWarmupTriggers, handoffKey);
  if (cached) return cached.promise;

  const body = normalizedWindows.length ? { protocolWindows: normalizedWindows } : {};
  const promise = axios.post(
    `${SEARCH_URL}/studies/${encodeURIComponent(studyInstanceUID)}/render-warmup`,
    body,
    { timeout: 3000 },
  ).then(res => res.data);
  putBoundedHandoff(studyWarmupTriggers, handoffKey, {
    promise,
    expiresAt: Date.now() + WARMUP_TRIGGER_TTL_MS,
  });
  promise.catch(() => {
    if (studyWarmupTriggers.get(handoffKey)?.promise === promise) {
      studyWarmupTriggers.delete(handoffKey);
    }
  });
  return promise;
};

export const getStudyInfo = async (studyInstanceUID) => {
  // Try Orthanc DICOMweb first (richer DICOM metadata)
  try {
    const res = await authAxios.get(`${DICOMWEB_URL}/studies?StudyInstanceUID=${studyInstanceUID}`);
    const s = (res.data || [])[0];
    if (s) {
      const t  = (tag) => s[tag]?.Value?.[0] ?? null;
      const tn = (tag) => s[tag]?.Value?.[0]?.Alphabetic ?? null;
      return {
        studyInstanceUID,
        patientName:      tn('00100010') || 'Unknown Patient',
        patientId:        t('00100020') || '',
        patientSex:       t('00100040') || '',
        patientAge:       t('00101010') || '',
        accessionNumber:  t('00080050') || '',
        studyDate:        t('00080020') || '',
        studyTime:        t('00080030') || '',
        studyDescription: t('00081030') || '',
        modalities:       (s['00080061']?.Value || []).join('/') || t('00080060') || '',
        institutionName:  t(INSTITUTION_NAME_TAG) || '',
      };
    }
  } catch (e) {
    if (isAuthorizationFailure(e)) throw e;
  }

  // Fall back to search service (disk-stored / anonymized studies)
  try {
    const res = await axios.get(`${SEARCH_URL}/studies`, { params: { study_instance_uid: studyInstanceUID, page_size: 1 } });
    const st = (res.data?.studies || [])[0];
    if (st) {
      return {
        studyInstanceUID,
        patientName:      st.patientName || 'Unknown Patient',
        patientId:        st.patientId || '',
        patientSex:       st.patientSex || st.patient_sex || st.sex || '',
        patientAge:       st.patientAge || st.patient_age || st.age || '',
        accessionNumber:  st.accessionNumber || st.accession_number || '',
        studyDate:        st.studyDate || '',
        studyTime:        st.studyTime || st.study_time || '',
        studyDescription: st.studyDescription || '',
        modalities:       st.modalities || '',
        institutionName:  st.institutionName || st.institution_name || '',
      };
    }
  } catch (e) {
    if (isAuthorizationFailure(e)) throw e;
    console.error('Error fetching study info:', e);
  }

  return null;
};

export const getStudyPriors = async (studyInstanceUID, params = {}) => {
  try {
    const qp = new URLSearchParams();
    if (params.limit) qp.set('limit', String(params.limit));
    const suffix = qp.toString() ? `?${qp.toString()}` : '';
    const res = await axios.get(`${SEARCH_URL}/studies/${encodeURIComponent(studyInstanceUID)}/priors${suffix}`);
    return res.data || [];
  } catch (e) {
    console.error('Error fetching priors:', e);
    throw e;
  }
};

const mapSearchSeries = (s, studyInstanceUID) => ({
  seriesInstanceUID: s.seriesInstanceUID,
  seriesNumber:      s.seriesNumber ?? '?',
  acquisitionNumber: s.acquisitionNumber ?? s.acquisition_number ?? s.AcquisitionNumber ?? '',
  seriesDescription: s.seriesDescription || 'No Description',
  protocolName:      s.protocolName || s.protocol_name || s.ProtocolName || '',
  modality:          s.modality || '??',
  laterality:        s.laterality || s.imageLaterality || '',
  viewPosition:      s.viewPosition || s.view_position || '',
  bodyPartExamined:  s.bodyPartExamined || s.body_part_examined || '',
  sliceThickness:    s.sliceThickness ?? s.slice_thickness ?? s.SliceThickness ?? '',
  pixelSpacing:      s.pixelSpacing ?? s.pixel_spacing ?? s.PixelSpacing ?? s.imagerPixelSpacing ?? s.ImagerPixelSpacing ?? '',
  manufacturer:      s.manufacturer ?? s.Manufacturer ?? s.manufacturerName ?? s.manufacturer_name ?? '',
  manufacturerModelName: s.manufacturerModelName ?? s.manufacturer_model_name ?? s.ManufacturerModelName ?? s.modelName ?? s.scannerModel ?? '',
  institutionName:  s.institutionName ?? s.institution_name ?? s.InstitutionName ?? '',
  stationName:      s.stationName ?? s.station_name ?? s.StationName ?? '',
  convolutionKernel: s.convolutionKernel ?? s.convolution_kernel ?? s.ConvolutionKernel ?? s.reconstructionKernel ?? s.reconstruction_kernel ?? '',
  patientPosition:   s.patientPosition || s.patient_position || s.PatientPosition || '',
  acquisitionDateTime: s.acquisitionDateTime || s.acquisition_datetime || s.AcquisitionDateTime || '',
  acquisitionDate:   s.acquisitionDate || s.acquisition_date || s.AcquisitionDate || '',
  acquisitionTime:   s.acquisitionTime || s.acquisition_time || s.AcquisitionTime || s.seriesTime || s.series_time || '',
  numInstances:      s.numInstances ?? '?',
  ingestStatus:      s.ingestStatus || s.ingest_status || s.processingStatus || s.processing_status || s.receiveStatus || s.receive_status || '',
  ingestPending:     s.ingestPending ?? s.ingest_pending ?? s.processingPending ?? s.processing_pending ?? s.instancesPending ?? s.instances_pending ?? s.imagesPending ?? s.images_pending ?? null,
  ingestRunning:     s.ingestRunning ?? s.ingest_running ?? s.processingRunning ?? s.processing_running ?? s.instancesRunning ?? s.instances_running ?? s.imagesRunning ?? s.images_running ?? null,
  expectedInstances: s.expectedInstances ?? s.expected_instances ?? s.expectedInstanceCount ?? s.expected_instance_count ?? s.instancesExpected ?? s.instances_expected ?? s.imagesExpected ?? s.images_expected ?? null,
  receivedInstances: s.receivedInstances ?? s.received_instances ?? s.numInstances ?? s.num_instances ?? null,
  studyInstanceUID,
  ...(Object.prototype.hasOwnProperty.call(s, 'presentationState')
    ? { presentationState: s.presentationState }
    : {}),
});

const fetchStudyOpenBundle = async (studyInstanceUID, params = {}) => {
  const res = await axios.get(
    `${SEARCH_URL}/studies/${encodeURIComponent(studyInstanceUID)}/open-bundle`,
    { params: { prior_limit: params.priorLimit ?? 20 } },
  );
  const bundle = res.data;
  if (bundle?.schemaVersion !== 1 || !bundle.study || !Array.isArray(bundle.series) || !Array.isArray(bundle.priors)) {
    throw new Error('Unsupported study-open bundle schema');
  }
  return {
    schemaVersion: 1,
    study: { ...bundle.study, institutionName: bundle.study.institutionName || '' },
    series: bundle.series.map((series) => mapSearchSeries(series, studyInstanceUID)),
    priors: bundle.priors,
    metadata: bundle.metadata || { seriesProjectionConsistent: false },
  };
};

const studyOpenBundleKey = (studyInstanceUID, params = {}) => (
  `${studyInstanceUID}\u0000${params.priorLimit ?? 20}`
);

export const prefetchStudyOpenBundle = (studyInstanceUID, params = {}) => {
  const key = studyOpenBundleKey(studyInstanceUID, params);
  const cached = getFreshHandoff(studyOpenBundleHandoffs, key);
  if (cached) return cached.promise;

  const promise = fetchStudyOpenBundle(studyInstanceUID, params);
  putBoundedHandoff(studyOpenBundleHandoffs, key, {
    promise,
    expiresAt: Date.now() + STUDY_OPEN_HANDOFF_TTL_MS,
  });
  promise.catch(() => {
    if (studyOpenBundleHandoffs.get(key)?.promise === promise) {
      studyOpenBundleHandoffs.delete(key);
    }
  });
  return promise;
};

export const getStudyOpenBundle = (studyInstanceUID, params = {}) => {
  const key = studyOpenBundleKey(studyInstanceUID, params);
  const prefetched = getFreshHandoff(studyOpenBundleHandoffs, key);
  if (prefetched) {
    studyOpenBundleHandoffs.delete(key);
    return prefetched.promise;
  }
  return fetchStudyOpenBundle(studyInstanceUID, params);
};

export const getSeries = async (studyInstanceUID, { skipSearch = false } = {}) => {
  // Try search service first — covers disk-stored studies that bypassed Orthanc
  if (!skipSearch) {
    try {
      const res = await axios.get(`${SEARCH_URL}/studies/${studyInstanceUID}/series`);
      if (res.data && res.data.length > 0) {
        return res.data.map((series) => mapSearchSeries(series, studyInstanceUID));
      }
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) throw e;
      // Explicitly unavailable search projection may fall through to DICOMweb.
    }
  }

  // Fall back to Orthanc DICOMweb
  try {
    const res = await authAxios.get(`${DICOMWEB_URL}/studies/${studyInstanceUID}/series`);
    return (res.data || []).map(s => ({
      seriesInstanceUID: tag(s, '0020000E'),
      seriesNumber:      tag(s, '00200011', '?'),
      acquisitionNumber: tag(s, '00200012'),
      seriesDescription: tag(s, '0008103E', 'No Description'),
      protocolName:      tag(s, '00181030'),
      modality:          tag(s, '00080060'),
      laterality:        tag(s, '00200060') || tag(s, '00200062'),
      viewPosition:      tag(s, '00185101'),
      bodyPartExamined:  tag(s, '00180015'),
      sliceThickness:    tag(s, '00180050'),
      pixelSpacing:      tag(s, '00280030') || tag(s, '00181164'),
      manufacturer:      tag(s, '00080070'),
      manufacturerModelName: tag(s, '00081090'),
      institutionName:  tag(s, INSTITUTION_NAME_TAG),
      stationName:      tag(s, '00081010'),
      convolutionKernel: tag(s, '00181210'),
      patientPosition:   tag(s, '00185100'),
      acquisitionDateTime: tag(s, '0008002A'),
      acquisitionDate:   tag(s, '00080022') || tag(s, '00080021'),
      acquisitionTime:   tag(s, '00080032') || tag(s, '00080031'),
      numInstances:      tag(s, '00201209', '?'),
      studyInstanceUID,
    })).filter(s => s.seriesInstanceUID);
  } catch (e) {
    if ([401, 403].includes(e?.response?.status)) throw e;
    console.error('Error fetching series:', e);
    return [];
  }
};

export const getSeriesMetadata = async (studyInstanceUID, seriesInstanceUID) => {
  try {
    const res = await authAxios.get(`${DICOMWEB_URL}/studies/${studyInstanceUID}/series/${seriesInstanceUID}/metadata`);
    return res.data || [];
  } catch (e) {
    console.error('Error fetching metadata:', e);
    return [];
  }
};

export const getRenderedImage = (studyInstanceUID, seriesInstanceUID, slice, ww = 0, wc = 0) =>
  `${RENDERING_URL}/study/${studyInstanceUID}/series/${seriesInstanceUID}?slice=${slice}&ww=${Math.round(ww)}&wc=${Math.round(wc)}`;


export const getSeriesCount = async (studyInstanceUID, seriesInstanceUID) => {
  try {
    const res = await axios.get(`${RENDERING_URL}/study/${studyInstanceUID}/series/${seriesInstanceUID}/count`);
    return res.data.count || 100;
  } catch { return 100; }
};

export const getPixelSpacing = async (studyInstanceUID, seriesInstanceUID) => {
  try {
    const res = await authAxios.get(
      `${DICOMWEB_URL}/studies/${studyInstanceUID}/series/${seriesInstanceUID}/metadata`
    );
    const instances = res.data || [];
    if (!instances.length) return null;
    const first = instances[0];
    const ps = first['00280030']?.Value || first['00181164']?.Value;
    if (ps && ps.length >= 2) return [parseFloat(ps[0]), parseFloat(ps[1])];
    return null;
  } catch { return null; }
};

export const getSeriesGeometry = async (studyInstanceUID, seriesInstanceUID, { signal } = {}) => {
  try {
    const res = await authAxios.get(
      `${DICOMWEB_URL}/studies/${studyInstanceUID}/series/${seriesInstanceUID}/metadata`,
      signal ? { signal } : undefined,
    );
    const instances = res.data || [];
    const geometry = instances.map((instance, metadataIndex) => ({
      sopInstanceUID: tag(instance, '00080018'),
      instanceNumber: tag(instance, '00200013'),
      imagePositionPatient: instance['00200032']?.Value || null,
      imageOrientationPatient: instance['00200037']?.Value || null,
      pixelSpacing: instance['00280030']?.Value || instance['00181164']?.Value || null,
      sliceThickness: tag(instance, '00180050'),
      spacingBetweenSlices: tag(instance, '00180088'),
      windowWidth: instance['00281051']?.Value || null,
      windowCenter: instance['00281050']?.Value || null,
      frameOfReferenceUID: tag(instance, '00200052'),
      rows: tag(instance, '00280010'),
      columns: tag(instance, '00280011'),
      metadataIndex,
    }));
    // Geometry arrays are indexed by the same slice number the renderer displays.
    // The normal render path orders direct-storage/WADO frames by InstanceNumber,
    // not by anatomical IPP projection. Some CT stacks acquire feet-to-head so
    // projected IPP order is the reverse of viewer slice order; sorting geometry
    // by IPP then makes reference lines use the wrong source plane (foot image,
    // pelvis reference line). Keep every frame and sort by render order instead;
    // patient-space helpers still use each frame's IPP/IOP when projecting.
    return geometry.sort((a, b) => {
      const ia = Number(a.instanceNumber);
      const ib = Number(b.instanceNumber);
      const aHasInstance = Number.isFinite(ia);
      const bHasInstance = Number.isFinite(ib);
      if (aHasInstance && bHasInstance && ia !== ib) return ia - ib;
      if (aHasInstance !== bHasInstance) return aHasInstance ? -1 : 1;
      return (a.metadataIndex ?? 0) - (b.metadataIndex ?? 0);
    });
  } catch {
    return [];
  }
};


// ─── MPR API ─────────────────────────────────────────────────────────────────

export const getMPRInfo = (studyId, seriesId) =>
  fetch(`${RENDERING_URL}/study/${studyId}/series/${seriesId}/mpr/info`).then(r => r.json());

export const getMPRStatus = (studyId, seriesId, { plane = '', slice = '', ww = 0, wc = 0, enqueue = false } = {}) => {
  const params = new URLSearchParams({
    ww: `${Math.round(ww)}`,
    wc: `${Math.round(wc)}`,
  });
  if (plane) params.set('plane', plane);
  if (slice !== '' && slice !== null && slice !== undefined) params.set('slice', `${slice}`);
  if (enqueue) params.set('enqueue', '1');
  return fetch(`${RENDERING_URL}/study/${studyId}/series/${seriesId}/mpr/status?${params}`).then(r => r.json());
};

export const getMPRUrl = (studyId, seriesId, plane, slice, ww, wc) =>
  `${RENDERING_URL}/study/${studyId}/series/${seriesId}/mpr?plane=${plane}&slice=${slice}&ww=${Math.round(ww)}&wc=${Math.round(wc)}&priority=interactive`;

export const getMIPUrl = (studyId, seriesId, plane, slice, slab, projection, ww, wc) =>
  `${RENDERING_URL}/study/${studyId}/series/${seriesId}/mip?plane=${plane}&slice=${slice}&slab=${slab}&projection=${projection}&ww=${Math.round(ww)}&wc=${Math.round(wc)}&priority=interactive`;

export const getMPRFusionInfo = (studyId, ctSeriesId, petSeriesId) =>
  fetch(`${RENDERING_URL}/study/${studyId}/series/${ctSeriesId}/mpr/fusion/info?petSeries=${encodeURIComponent(petSeriesId)}`).then(r => r.json());

export const warmMPRFusion = (studyId, ctSeriesId, petSeriesId, { planes = 'axial,coronal,sagittal', ww = 0, wc = 0, opacity = 0.45, colormap = 'hot', petSlabMm = 1, center = '', maxFrames = 24 } = {}) => {
  const params = new URLSearchParams({
    petSeries: petSeriesId,
    planes,
    ww: `${Math.round(ww)}`,
    wc: `${Math.round(wc)}`,
    opacity: `${opacity}`,
    colormap,
    petSlabMm: `${petSlabMm}`,
    maxFrames: `${maxFrames}`,
    priority: 'background',
  });
  if (center !== '' && center !== null && center !== undefined) params.set('center', `${center}`);
  return fetch(`${RENDERING_URL}/study/${studyId}/series/${ctSeriesId}/mpr/fusion/warm?${params}`).then(r => r.json());
};

export const getMPRFusionUrl = (studyId, ctSeriesId, petSeriesId, plane, slice, ww, wc, opacity = 0.45, colormap = 'hot', petSlabMm = 1) =>
  `${RENDERING_URL}/study/${studyId}/series/${ctSeriesId}/mpr/fusion?petSeries=${encodeURIComponent(petSeriesId)}&plane=${plane}&slice=${slice}&ww=${Math.round(ww)}&wc=${Math.round(wc)}&opacity=${opacity}&colormap=${encodeURIComponent(colormap)}&petSlabMm=${petSlabMm}&priority=interactive`;


// ─── User Preference API ─────────────────────────────────────────────────────

export const userPreferenceApi = {
  list:  () => axios.get(`${ADMIN_URL}/user/preferences`).then(r => r.data),
  get:   (namespace) => axios.get(`${ADMIN_URL}/user/preferences/${encodeURIComponent(namespace)}`).then(r => r.data),
  save:  (namespace, value) => axios.put(`${ADMIN_URL}/user/preferences/${encodeURIComponent(namespace)}`, { value }).then(r => r.data),
  reset: (namespace) => axios.delete(`${ADMIN_URL}/user/preferences/${encodeURIComponent(namespace)}`),
};

// ─── Presentation State API ─────────────────────────────────────────────────

export const presentationStateApi = {
  get: (studyInstanceUID, seriesInstanceUID) => axios.get(`${SEARCH_URL}/studies/${encodeURIComponent(studyInstanceUID)}/presentation-state`, { params: { series_iuid: seriesInstanceUID } }).then(r => r.data),
  save: (studyInstanceUID, seriesInstanceUID, state, expectedVersion = 0) => axios.put(`${SEARCH_URL}/studies/${encodeURIComponent(studyInstanceUID)}/presentation-state`, { seriesInstanceUID, state, expectedVersion }).then(r => r.data),
};

// ─── Native Reporting API ────────────────────────────────────────────────────

export const reportingApi = {
  get: (studyInstanceUID) => axios.get(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report`).then(r => r.data),
  saveDraft: (studyInstanceUID, report) => axios.put(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report`, report).then(r => r.data),
  finalize: (studyInstanceUID, report) => axios.post(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report/finalize`, report).then(r => r.data),
  amend: (studyInstanceUID, report) => axios.post(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report/amend`, report).then(r => r.data),
  history: (studyInstanceUID) => axios.get(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report/history`).then(r => r.data),
  templates: (params = {}) => axios.get(`${REPORTING_URL}/report-templates`, { params }).then(r => r.data),
  insertText: (studyInstanceUID, payload) => axios.post(`${REPORTING_URL}/studies/${encodeURIComponent(studyInstanceUID)}/report/insert`, payload).then(r => r.data),
};


// ─── Viewer QC Ticket API ───────────────────────────────────────────────────

const isValidQcTicketResponse = (data) => Boolean(
  data
  && typeof data === 'object'
  && !Object.prototype.hasOwnProperty.call(data, 'error')
  && !Object.prototype.hasOwnProperty.call(data, 'statusCode')
  && (data.id || data.ticketId || data.ticket_id || data.status === 'queued' || data.status === 'created' || data.status === 'ok')
);

export const qcTicketApi = {
  create: (payload) => axios.post(`${QC_TICKET_URL}/qc/tickets`, payload).then((r) => {
    if (!isValidQcTicketResponse(r.data)) {
      throw new Error('QC ticket service returned an unexpected response; ticket was not confirmed.');
    }
    return r.data;
  }),
};


// ─── Admin API ────────────────────────────────────────────────────────────────

export const adminApi = {
  listUsers:          () => axios.get('/auth/users').then(r => r.data),
  searchProfileUsers: (q = '', limit = 20, offset = 0) => axios.get(`${ADMIN_URL}/profile-preferences/users/search`, { params: { q, limit, offset } }).then(r => r.data),

  // Stats
  getOverview:       () => axios.get(`${ADMIN_URL}/stats/overview`).then(r => r.data),
  getFacilityStats:  () => axios.get(`${ADMIN_URL}/stats/facilities`).then(r => r.data),
  getMonitoringHealth: () => axios.get(`${ADMIN_URL}/monitoring/health`).then(r => r.data),
  getStudyPerformance: (params = {}) => axios.get(`${ADMIN_URL}/monitoring/study-opens`, { params }).then(r => r.data),
  searchStudyPerformanceUsers: (query, limit = 10) => axios.post('/auth/users/search/performance', { query, limit }).then(r => r.data),
  queryStudyPerformance: (payload) => axios.post(`${ADMIN_URL}/monitoring/study-opens/query`, payload).then(r => r.data),
  submitStudyPerformance: (payload, options = {}) => axios.post(`${ADMIN_URL}/monitoring/study-opens`, payload, options).then(r => r.data),
  submitStudyInteractionPerformance: (payload, options = {}) => axios.post(`${ADMIN_URL}/monitoring/study-opens/interaction`, payload, options).then(r => r.data),

  // Admin-managed target-user profile preferences
  listProfilePreferences: (userId) => axios.get(`${ADMIN_URL}/profile-preferences/users/${encodeURIComponent(userId)}`).then(r => r.data),
  getProfilePreference: (userId, namespace) => axios.get(`${ADMIN_URL}/profile-preferences/users/${encodeURIComponent(userId)}/${encodeURIComponent(namespace)}`).then(r => r.data),
  saveProfilePreference: (userId, namespace, value) => axios.put(`${ADMIN_URL}/profile-preferences/users/${encodeURIComponent(userId)}/${encodeURIComponent(namespace)}`, { value }).then(r => r.data),
  resetProfilePreference: (userId, namespace) => axios.delete(`${ADMIN_URL}/profile-preferences/users/${encodeURIComponent(userId)}/${encodeURIComponent(namespace)}`),

  // Storage backends
  getStorageBackends: () => axios.get(`${ADMIN_URL}/storage/backends`).then(r => r.data),
  createStorageBackend: (data) => axios.post(`${ADMIN_URL}/storage/backends`, data).then(r => r.data),
  updateStorageBackend: (id, data) => axios.put(`${ADMIN_URL}/storage/backends/${id}`, data).then(r => r.data),
  deleteStorageBackend: (id) => axios.delete(`${ADMIN_URL}/storage/backends/${id}`),

  // Facilities
  getFacilities:     () => axios.get(`${ADMIN_URL}/facilities`).then(r => r.data),
  syncFacilities:    () => axios.post(`${ADMIN_URL}/facilities/sync`).then(r => r.data),
  createFacility:    (data) => axios.post(`${ADMIN_URL}/facilities`, data).then(r => r.data),
  updateFacility:    (id, data) => axios.put(`${ADMIN_URL}/facilities/${id}`, data).then(r => r.data),
  deleteFacility:    (id) => axios.delete(`${ADMIN_URL}/facilities/${id}`),

  // DICOM AE Titles
  getDicomAeTitles:  () => axios.get(`${ADMIN_URL}/dicom/ae-titles`).then(r => r.data),
  createDicomAeTitle: (data) => axios.post(`${ADMIN_URL}/dicom/ae-titles`, data).then(r => r.data),
  updateDicomAeTitle: (id, data) => axios.put(`${ADMIN_URL}/dicom/ae-titles/${id}`, data).then(r => r.data),
  syncDicomAeTitle: (id) => axios.post(`${ADMIN_URL}/dicom/ae-titles/${id}/sync-dcm4chee`).then(r => r.data),
  deleteDicomAeTitle: (id) => axios.delete(`${ADMIN_URL}/dicom/ae-titles/${id}`),

  // LCM Rules
  getLcmRules:       () => axios.get(`${ADMIN_URL}/lcm/rules`).then(r => r.data),
  createLcmRule:     (data) => axios.post(`${ADMIN_URL}/lcm/rules`, data).then(r => r.data),
  updateLcmRule:     (id, data) => axios.put(`${ADMIN_URL}/lcm/rules/${id}`, data).then(r => r.data),
  deleteLcmRule:     (id) => axios.delete(`${ADMIN_URL}/lcm/rules/${id}`),
  previewLcmRule:    (id) => axios.post(`${ADMIN_URL}/lcm/rules/${id}/preview`).then(r => r.data),

  // Config
  getConfig:         (cat) => axios.get(`${ADMIN_URL}/config`, { params: cat ? { category: cat } : {} }).then(r => r.data),
  updateConfig:      (key, value) => axios.put(`${ADMIN_URL}/config/${key}`, null, { params: { value } }).then(r => r.data),
  getRenderCachePolicy: () => axios.get(`${ADMIN_URL}/render-cache/policy`).then(r => r.data),
  updateRenderCachePolicy: (data) => axios.put(`${ADMIN_URL}/render-cache/policy`, data).then(r => r.data),
  applyRenderCachePolicy: () => axios.post(`${ADMIN_URL}/render-cache/policy/apply`).then(r => r.data),

  // Audit
  getAudit:          (params) => axios.get(`${ADMIN_URL}/audit`, { params }).then(r => r.data),

  // Study Access Audit
  auditStudyAccess:  (studyInstanceUID, seriesInstanceUID, patientId, accessMethod) => {
    return axios.post('/auth/audit/study-access', {
      study_uid: studyInstanceUID,
      series_uid: seriesInstanceUID,
      patient_id: patientId,
      access_method: accessMethod,
    }).catch(e => console.warn('auditStudyAccess failed:', e));
  },
};
